import { useEffect, useState } from "react";
import { Download, Share2, Copy, Check } from "lucide-react";

/**
 * Post-checkout share card. Displays the auto-generated PNG (backend PIL) and
 * offers Download + Web-Share + Copy-caption actions. Only shown for merch or
 * pledge purchases (skipped for ticket / founder-pitch flows).
 */
export default function ShareCard({ sessionId, productType, amountCents, itemName }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const cardUrl = `${backendUrl}/api/share-card/${sessionId}.png`;

  useEffect(() => {
    // Cache-bust once so the browser refetches after payment_status flips to paid
    setImgUrl(`${cardUrl}?t=${Date.now()}`);
  }, [cardUrl]);

  const dollars = Math.round((amountCents || 0) / 100);
  const shortName = itemName ? itemName.replace(/^Black Dollars United\s*[—-]\s*/, "") : null;

  const caption = productType === "pledge"
    ? `I just funded the Trillion Dollar Spending Power Fund — $${dollars} into the movement. 🖤💛\n\n@BlackDollarsUnited is pooling $50M in 2 years to build Black-owned banks, grocery stores, manufacturing plants, and food franchises. One fund. One people.\n\nJoin us → blackdollarsunited.org\n\n#BlackDollarsUnited #TrillionDollarSpendingPower #BlackWealth #BlackEconomicPower #BuyBlack`
    : `I just copped the ${shortName || "Black Dollars United drop"} — $${dollars} straight into the Trillion Dollar Spending Power Fund. 🖤💛\n\nEvery tee, cap, and hoodie funds Black-owned business at scale. $50M in 2 years. One fund. One people.\n\nShop the movement → blackdollarsunited.org\n\n#BlackDollarsUnited #TrillionDollarSpendingPower #WearTheMovement #BuyBlack #BlackWealth`;

  const handleDownload = async () => {
    try {
      const res = await fetch(cardUrl, { cache: "no-store" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bdu-${sessionId.slice(-12)}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // Fallback — just open the image
      window.open(cardUrl, "_blank");
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const res = await fetch(cardUrl, { cache: "no-store" });
      const blob = await res.blob();
      const file = new File([blob], `bdu-${sessionId.slice(-12)}.png`, { type: "image/png" });
      // Prefer native Web Share (mobile) with the actual image
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Black Dollars United",
          text: caption,
        });
        return;
      }
      // Fallback: open a mailto or twitter intent so desktop users still get somewhere
      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        productType === "pledge"
          ? `I just funded the Trillion Dollar Spending Power Fund. $50M in 2 years. Join us → blackdollarsunited.org`
          : `I just copped Black Dollars United. Every drop funds the Trillion Dollar Fund. → blackdollarsunited.org`
      )}`;
      window.open(twitterUrl, "_blank", "noopener");
    } catch (e) {
      // user cancelled or share failed silently
    } finally {
      setSharing(false);
    }
  };

  const handleCopyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      // Fallback: select the text so they can copy manually
      window.prompt("Copy this caption:", caption);
    }
  };

  return (
    <div className="mt-14 border border-bdu-gold/20 bg-bdu-obsidian/60 p-6 sm:p-10" data-testid="share-card-section">
      <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>Turn this moment into a movement</div>
      <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-bdu-cream">
        Share your receipt. <span className="italic text-bdu-gold">Recruit the next 3.</span>
      </h2>
      <p className="mt-3 text-sm text-bdu-cream/60 max-w-xl">
        Your purchase already moved us. Post this card and every 3 people who see it, one joins the fund.
      </p>

      <div className="mt-8 grid md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-8 items-start">
        {/* PREVIEW */}
        <div className="border border-bdu-gold/30 shadow-2xl overflow-hidden bg-black" data-testid="share-card-preview">
          {imgUrl ? (
            <img
              src={imgUrl}
              alt="Your BDU share card"
              className="w-full h-auto block"
              onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
            />
          ) : (
            <div className="aspect-square flex items-center justify-center text-bdu-cream/40">Generating…</div>
          )}
        </div>

        {/* ACTIONS + CAPTION */}
        <div className="flex flex-col gap-4">
          <button
            onClick={handleDownload}
            className="btn-gold w-full justify-center"
            data-testid="share-card-download"
          >
            <Download size={16} /> Download Card
          </button>
          <button
            onClick={handleShare}
            disabled={sharing}
            className="btn-ghost w-full justify-center"
            data-testid="share-card-share"
          >
            <Share2 size={16} /> {sharing ? "Opening share…" : "Share to Socials"}
          </button>

          <div className="mt-2 border border-bdu-gold/15 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow" style={{ fontSize: "0.55rem" }}>Ready-to-post caption</div>
              <button
                onClick={handleCopyCaption}
                className="text-xs uppercase tracking-widest text-bdu-gold hover:underline inline-flex items-center gap-1.5"
                data-testid="share-card-copy-caption"
              >
                {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
              </button>
            </div>
            <div className="text-xs text-bdu-cream/70 whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto">
              {caption}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
